/**
 * **The list page** (C14 §10.6 items 1–2, ADR-0033 §1; interface I-1).
 *
 *     <ListPage title="Imports" description="…" primary={<Button>…</Button>}
 *       loading={loading} error={error} isEmpty={list.isEmpty}
 *       empty={{ title: 'Nothing imported yet', description: '…', action: <Button>…</Button> }}
 *       toolbar={<ListToolbar list={list} />}>
 *       <ListTable list={list} rowKey={(r) => r.id} rowHref={(r) => `/imports/${r.id}`} />
 *     </ListPage>
 *
 * Four states, one place: **loading** (a toolbar-shaped bar and the table's
 * skeleton rows — `ListTable` reads `loading` from here), **failed** (an
 * `Alert`, never an empty shelf), **nothing exists** (`EmptyState` with one
 * action, and no toolbar to search nothing with), and the list itself —
 * whose *filtered to nothing* state is `ListTable`'s `NoMatches`.
 */

import { Alert, EmptyState, PageHeader, Skeleton } from '@fiveox/ui'
import { ArrowLeft } from 'lucide-react'
import { createContext, type ReactNode, useContext } from 'react'
import { Link } from 'react-router'

export interface PageBack {
  /** Where the back link goes — a hub, a list. */
  to: string
  /** What it reads — *All reports*, *Imports*. */
  label: string
}

export interface ListEmpty {
  /** A noun phrase — *No imports yet*. */
  title: ReactNode
  /** What will appear here, and how it gets here. */
  description: ReactNode
  /** The one next step. */
  action?: ReactNode
}

export interface ListPageProps {
  title: ReactNode
  /** One sentence; let it carry the count. */
  description?: ReactNode
  /** The page's one primary action (a `Button`). */
  primary?: ReactNode
  /** Usually `<ListToolbar list={list} />`. Hidden while loading or empty. */
  toolbar?: ReactNode
  loading: boolean
  /** A failed read, in the firm's words. Shown instead of the empty state. */
  error?: string | null
  /** What to say when nothing exists. */
  empty: ListEmpty
  /** Whether nothing exists — `list.isEmpty`. The empty state shows only once
   *  loading is done, and never over an error. */
  isEmpty?: boolean
  /** A list under a hub or a record (a report under Reports). */
  back?: PageBack
  children: ReactNode
}

const ListLoading = createContext(false)

/** Whether the enclosing `ListPage` is loading — `ListTable` draws its
 *  skeleton rows from this. */
export function useListLoading(): boolean {
  return useContext(ListLoading)
}

export function BackLink({ back }: { back: PageBack }) {
  return (
    <Link
      to={back.to}
      className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium"
    >
      <ArrowLeft size={14} aria-hidden /> {back.label}
    </Link>
  )
}

export function ListPage({
  title,
  description,
  primary,
  toolbar,
  loading,
  error,
  empty,
  isEmpty = false,
  back,
  children,
}: ListPageProps) {
  const showEmpty = !loading && !error && isEmpty
  // A failed first read has nothing to show under the alert; a failed
  // *refresh* keeps the rows it had.
  const showBody = !showEmpty && !(error && isEmpty && !loading)

  return (
    <div data-list-page="" className="w-full min-w-0 space-y-6">
      <PageHeader
        back={back ? <BackLink back={back} /> : undefined}
        title={title}
        description={description}
        actions={primary}
      />

      {error && <Alert tone="error">{error}</Alert>}

      {showEmpty && (
        <EmptyState title={empty.title} description={empty.description} action={empty.action} />
      )}

      {showBody && (
        <ListLoading.Provider value={loading}>
          <div className="min-w-0 space-y-4">
            {toolbar &&
              (loading ? (
                <div role="status" aria-busy aria-label="Loading" className="flex gap-3">
                  <Skeleton className="h-9 w-full rounded-lg sm:w-64" />
                  <Skeleton className="hidden h-9 w-36 rounded-lg sm:block" />
                </div>
              ) : (
                toolbar
              ))}
            {children}
          </div>
        </ListLoading.Provider>
      )}
    </div>
  )
}
